import { z } from 'zod';
import type { AISchemaField, UISchemaField } from '../types/index';

export const heroAISchema: AISchemaField[] = [
  { name: 'eyebrow', type: 'string', required: false, max: 30 },
  { name: 'headline', type: 'string', required: true, max: 120, hint: 'Punchy, outcome-led, ideal max 8 words. 改行 \\n で 2 行に分けて強調可能（日本語キャッチコピー向け）' },
  { name: 'subheadline', type: 'string', required: true, max: 200, hint: '1-2 sentences expanding on the headline' },
  { name: 'primaryCta', type: 'string', required: true, max: 24, hint: 'e.g. "Get started"' },
  { name: 'primaryCtaHref', type: 'url', required: false },
  { name: 'secondaryCta', type: 'string', required: false, max: 24 },
  { name: 'secondaryCtaHref', type: 'url', required: false },
  { name: 'trustLine', type: 'string', required: false, max: 80, hint: 'e.g. "Trusted by 10,000+ teams"' },
  { name: 'heroImage', type: 'image_url', required: false },
  // ── event variant 用フィールド（任意） ──
  { name: 'eventDate', type: 'string', required: false, max: 60, hint: 'イベント日時（例: 2026.05.08(木) 19:00–21:00）。event variant のみ' },
  { name: 'eventLocation', type: 'string', required: false, max: 80, hint: '開催地（例: 東京・赤坂 / オンライン）。event variant のみ' },
  { name: 'eventPrice', type: 'string', required: false, max: 30, hint: '料金（例: 無料 / 5,000円 / 要相談）。event variant のみ' },
  {
    name: 'eventSpeakers',
    type: 'array<object>',
    required: false,
    fields: [
      { name: 'name', type: 'string', required: true, max: 40 },
      { name: 'title', type: 'string', required: false, max: 60 },
      { name: 'avatar', type: 'image_url', required: false },
    ],
  },
];

export const heroUISchema: UISchemaField[] = [
  { name: 'eyebrow', type: 'text', label: 'アイブロウ', max: 30 },
  { name: 'headline', type: 'textarea', label: 'タイトル (改行可)', required: true, max: 120, hint: '改行で 2 段以上に分けて見出しを強調できます' },
  { name: 'subheadline', type: 'textarea', label: 'サブタイトル / キャッチフレーズ', required: true, max: 200 },
  { name: 'primaryCta', type: 'text', label: 'プライマリ CTA', required: true, max: 24 },
  { name: 'primaryCtaHref', type: 'cta_url', label: 'プライマリ CTA リンク' },
  { name: 'secondaryCta', type: 'text', label: 'セカンダリ CTA', max: 24 },
  { name: 'secondaryCtaHref', type: 'cta_url', label: 'セカンダリ CTA リンク' },
  { name: 'trustLine', type: 'text', label: '信頼バッジ文', max: 80 },
  { name: 'heroImage', type: 'image', label: '背景画像' },
  // event variant 用
  { name: 'eventDate', type: 'text', label: '日時 (event)', max: 60, placeholder: '例: 2026.05.08(木) 19:00' },
  { name: 'eventLocation', type: 'text', label: '開催地 (event)', max: 80, placeholder: '例: 東京・赤坂' },
  { name: 'eventPrice', type: 'text', label: '料金 (event)', max: 30, placeholder: '例: 無料 / 5,000円' },
  { name: 'eventSpeakers', type: 'team_list', label: '登壇者 (event)' },
];

const eventSpeakerSchema = z.object({
  name: z.string().max(40),
  title: z.string().max(60).optional().nullable(),
  avatar: z.string().optional().nullable(),
});

export const heroZodSchema = z.object({
  eyebrow: z.string().max(30).optional(),
  // 改行を含めて max 120 文字（旧データ互換のため上限を緩和）
  headline: z.string().min(1).max(120),
  subheadline: z.string().min(1).max(200),
  primaryCta: z.string().min(1).max(24),
  primaryCtaHref: z.string().optional().nullable(),
  secondaryCta: z.string().max(24).optional().nullable(),
  secondaryCtaHref: z.string().optional().nullable(),
  trustLine: z.string().max(80).optional().nullable(),
  heroImage: z.string().optional().nullable(),
  eventDate: z.string().max(60).optional().nullable(),
  eventLocation: z.string().max(80).optional().nullable(),
  eventPrice: z.string().max(30).optional().nullable(),
  eventSpeakers: z.array(eventSpeakerSchema).max(6).optional().nullable(),
});

export type HeroContent = z.infer<typeof heroZodSchema>;

export const heroSample: HeroContent = {
  eyebrow: 'New release',
  headline: 'Built for the work that matters.',
  subheadline:
    'Designed in California. Engineered for the studios where the work actually happens.',
  primaryCta: 'Configure',
  primaryCtaHref: '#',
  secondaryCta: 'Watch film',
  secondaryCtaHref: '#',
  trustLine: 'Trusted by 10,000+ creative teams',
  heroImage: null,
};
